import { useState, useEffect, useRef, useCallback } from 'react'
import { apiGet } from '../../../../generic/Api_Functions'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'

/**
 * Custom hook to manage KeyValueWidget data fetching
 * @param {string} key - The API key to fetch data for
 * @param {number} refreshInterval - Refresh interval in milliseconds (default 5000)
 * @returns {Object} - { value, loading, error, refresh }
 */
export const useKeyValueData = (key, refreshInterval = 5000) => {
  const { t_ } = useSmartTranslation()
  const [value, setValue] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const intervalRef = useRef(null)

  const fetchData = useCallback(async () => {
    if (!key) {
      setError(t_ ? t_('No key configured') : 'No key configured')
      setLoading(false)
      return
    }

    try {
      // Only set loading on first fetch or if explicit refresh needed,
      // but for interval updates we might not want to show spinner every time.
      // However, original code did setLoading(true) on key change.
      // We'll handle initial loading separately if needed, but here we just fetch.

      const result = await apiGet(`mos/getDeviceLastHistoryValue/${key}`)

      // Handle response structure
      const finalValue = result?.value !== undefined ? result.value : result

      setValue(finalValue)
      setError(null)
    } catch (err) {
      console.error('Error fetching key value:', err)
      setError(t_ ? t_('Error loading data') : 'Error loading data')
    } finally {
      setLoading(false)
    }
  }, [key, t_])

  useEffect(() => {
    // Reset state when key changes
    if (key) {
      setLoading(true)
      setValue(null)
      setError(null)
      fetchData()
    } else {
      setValue(null)
      setError(t_ ? t_('No key configured') : 'No key configured')
    }

    // Set up interval
    if (key && refreshInterval > 0) {
      intervalRef.current = setInterval(fetchData, refreshInterval)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [key, refreshInterval, fetchData, t_])

  return { value, loading, error, refresh: fetchData }
}
